import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers/OrgConfigurationProvider';
import {
  ButtonVariants,
  Dialog,
  FormGroup,
  IconName,
  Input,
  MixpanelEvent,
  MixpanelEventProperty,
  NotificationVariants,
  Text,
  useGlobalToasts,
  useMixpanel,
  VStack,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';

interface KillSwitchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const KillSwitchModal = ({ isOpen, onClose }: KillSwitchModalProps) => {
  const mixpanel = useMixpanel();
  const { getConfig, disableTrading, enableTrading } = useOrgConfiguration();
  const { add: addToast } = useGlobalToasts();
  const [isRequestLoading, setRequestLoading] = useState(false);
  const [confirm, setConfirm] = useState('');

  const isTradingDisabled = getConfig(OrgConfigurationKey.TradingDisableAll, '0') === '1';

  const handleEnableTrading = useCallback(() => {
    setRequestLoading(true);
    return enableTrading()
      .then(() => {
        setRequestLoading(false);
        mixpanel.track(MixpanelEvent.KillSwitch, { [MixpanelEventProperty.Enabled]: true });
        addToast({
          timeout: 5000,
          variant: NotificationVariants.Positive,
          text: 'Enabled trading.',
          dismissable: true,
        });
        onClose();
      })
      .catch((e: ErrorEvent) => {
        setRequestLoading(false);
        addToast({
          timeout: 5000,
          variant: NotificationVariants.Negative,
          text: e?.message || 'Could not enable trading.',
          dismissable: true,
        });
      });
  }, [addToast, enableTrading, mixpanel, onClose]);

  const handleDisableTrading = useCallback(() => {
    setRequestLoading(true);
    return disableTrading()
      .then(() => {
        setRequestLoading(false);
        mixpanel.track(MixpanelEvent.KillSwitch, { [MixpanelEventProperty.Enabled]: false });
        addToast({
          timeout: 5000,
          variant: NotificationVariants.Positive,
          text: 'Disabled trading.',
          dismissable: true,
        });
        onClose();
      })
      .catch((e: ErrorEvent) => {
        setRequestLoading(false);
        addToast({
          timeout: 5000,
          variant: NotificationVariants.Negative,
          text: e?.message || 'Could not disable trading.',
          dismissable: true,
        });
      });
  }, [addToast, disableTrading, mixpanel, onClose]);

  const handleClose = useCallback(() => {
    setConfirm('');
    onClose();
  }, [onClose]);

  if (isTradingDisabled) {
    return (
      <Dialog
        variant={ButtonVariants.Positive}
        showClose={true}
        isOpen={isOpen}
        close={handleClose}
        confirmLabel="Enable Trading"
        headerIcon={IconName.CheckCircleSolid}
        title="Enable Trading"
        onConfirm={handleEnableTrading}
        confirmDisabled={isRequestLoading}
        width={500}
      >
        <VStack gap="spacing12" alignItems="flex-start" w="100%">
          <Text color="colorTextPrimaryButton" textAlign="left" w="100%">
            You are about to enable trading for all users. <br /> Please confirm the action.
          </Text>
        </VStack>
      </Dialog>
    );
  }

  return (
    <Dialog
      variant={ButtonVariants.Negative}
      showClose={true}
      isOpen={isOpen}
      close={handleClose}
      confirmLabel="Disable Trading & Cancel All Orders"
      headerIcon={IconName.ExclamationSolid}
      title="Disable Trading"
      confirmDisabled={confirm !== 'confirm' || isRequestLoading}
      onConfirm={handleDisableTrading}
      width={500}
    >
      <VStack gap="spacing12" alignItems="flex-start">
        <Text color="colorTextPrimaryButton" textAlign="left">
          You are about to cancel all open orders and disable trading for all users. <br /> Please confirm the action.
        </Text>
        <VStack w="100%">
          <FormGroup label='To confirm, type "confirm" below:' w="100%">
            <Input value={confirm} autoComplete="off" onChange={e => setConfirm(e.target.value)} />
          </FormGroup>
        </VStack>
      </VStack>
    </Dialog>
  );
};
